/**
 * Access Denied Page Component
 * 
 * Displays when a user tries to access a page they don't have privileges for.
 */

import * as React from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldX, Home, ArrowLeft } from "lucide-react";
import type { AdminPrivilege } from "@/services/adminService";

interface AccessDeniedPageProps {
  requiredPrivileges?: AdminPrivilege[];
  message?: string;
}

const privilegeLabels: Record<AdminPrivilege, string> = {
  manage_users: "Kelola User",
  manage_user_reset_password: "Reset password anggota",
  manage_user_set_email: "Set email anggota",
  manage_locations: "Kelola Lokasi",
  manage_payments: "Kelola Pembayaran",
  manage_blocks: "Kelola Blokir",
  manage_silentkey: "Silent Key",
  manage_jabatan: "Kelola Jabatan",
  manage_admin_assignments: "Kelola Penugasan Admin",
  view_reports: "Lihat Laporan",
  manage_transactions: "Kelola Transaksi",
  manage_transactions_plan: "Kelola Rencana Transaksi",
  create_transaction_plan: "Buat Rencana Transfer",
  manage_historical_transfer: "Riwayat Transfer",
  manage_transfer_plans: "Kelola Daftar Rencana Transfer",
  create_transaction_bank_account: "Tambah Rekening Bank Transaksi",
};

export function AccessDeniedPage({ 
  requiredPrivileges = [],
  message 
}: AccessDeniedPageProps) {
  const [, setLocation] = useLocation();

  const handleGoHome = () => {
    setLocation("/user");
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-red-100 dark:bg-red-950/20 p-4">
              <ShieldX className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">Akses Ditolak</CardTitle>
          <CardDescription className="text-base mt-2">
            {message || "Anda tidak memiliki izin untuk mengakses halaman ini."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {requiredPrivileges.length > 0 && (
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">Hak Akses yang Diperlukan:</p>
              <ul className="list-disc list-inside space-y-1">
                {requiredPrivileges.map((privilege) => (
                  <li key={privilege} className="text-sm text-muted-foreground">
                    {privilegeLabels[privilege] || privilege}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={handleGoHome} className="w-full">
              <Home className="h-4 w-4 mr-2" />
              Kembali ke Profil
            </Button>
            <Button onClick={handleGoBack} variant="outline" className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

